import type {
  CasePriority,
  CaseSeverity,
  CaseStatus,
  LabelColor,
  ProjectIcon,
  ProjectStatus,
  RunItemStatus,
  RunStatus,
  UserRole,
} from '@tms/shared';
import { useMemo } from 'react';
import { useT } from './useT';

/**
 * Enum values come from the API; their labels are presentation, so they live in
 * the dictionaries rather than in the shared contract.
 */
export interface EnumLabels {
  role: (value: UserRole) => string;
  projectStatus: (value: ProjectStatus) => string;
  priority: (value: CasePriority) => string;
  severity: (value: CaseSeverity) => string;
  caseStatus: (value: CaseStatus) => string;
  runStatus: (value: RunStatus) => string;
  itemStatus: (value: RunItemStatus) => string;
  /** Yes / No for the automation flag of a test case. */
  automation: (value: boolean) => string;
  labelColor: (value: LabelColor) => string;
  /** What a project's icon shows, for readers who cannot see it. */
  projectIcon: (value: ProjectIcon) => string;
}

export const useEnumLabels = (): EnumLabels => {
  const { t } = useT();

  return useMemo(
    () => ({
      role: (value) => t(`enum.role.${value}`),
      projectStatus: (value) => t(`enum.projectStatus.${value}`),
      priority: (value) => t(`enum.priority.${value}`),
      severity: (value) => t(`enum.severity.${value}`),
      caseStatus: (value) => t(`enum.caseStatus.${value}`),
      runStatus: (value) => t(`enum.runStatus.${value}`),
      itemStatus: (value) => t(`enum.itemStatus.${value}`),
      automation: (value) => t(value ? 'enum.automated.yes' : 'enum.automated.no'),
      labelColor: (value) => t(`enum.labelColor.${value}`),
      projectIcon: (value) => t(`enum.projectIcon.${value}`),
    }),
    [t],
  );
};
